/* ==========================================================================
   Kintsugi — WebGL2 hero shader

   Why raw WebGL and not three.js: this draws a single full-screen quad with one
   fragment shader. three.js would add ~600KB to do the same job. The whole
   renderer below is under 4KB minified and has no dependencies.

   The image: clear blue water — calm, transparent, still. Per the author's
   request (Sep 2026) the cover must read as clarity and excellence, not the
   turbid ciénaga. The swamp/sediment layers and the gold crack seams were
   removed; the ciénaga now lives only in its own chapter, where it belongs.

   Safety rails, in order of how likely they are to matter:
     - Bails out entirely if WebGL2 is unavailable, leaving the CSS/poster hero.
     - Pauses the rAF loop when scrolled out of view (IntersectionObserver).
     - Pauses when the tab is hidden.
     - Recovers from GPU context loss instead of leaving a dead black canvas.
     - Caps DPR at 1.75 — a fragment shader is fill-rate bound, so native 3x on
       a phone triples cost for no perceptible gain.
   ========================================================================== */

(function () {
  'use strict';

  var VERT = [
    '#version 300 es',
    'in vec2 aPos;',
    'void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    '#version 300 es',
    'precision highp float;',
    'out vec4 outColor;',
    'uniform vec2  uRes;',
    'uniform float uTime;',
    'uniform float uProgress;',   // 0..1 scroll through the hero
    'uniform vec2  uPointer;',    // -1..1
    'uniform vec3  uInk;',
    'uniform vec3  uDeep;',
    'uniform vec3  uGold;',
    'uniform vec3  uGoldLight;',
    'uniform vec3  uSilt;',
    'uniform float uIntensity;',
    'uniform sampler2D uPhoto;',  // el paisaje real (la foto del mar)
    'uniform float uHasPhoto;',   // 1 si la textura cargó bien
    'uniform float uWaterLine;',  // altura del agua en la foto (0 abajo, 1 arriba)

    // --- hash / noise -----------------------------------------------------
    'vec2 hash2(vec2 p){',
    '  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));',
    '  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);',
    '}',

    'float noise(vec2 p){',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(dot(hash2(i + vec2(0.0,0.0)), f - vec2(0.0,0.0)),',
    '                 dot(hash2(i + vec2(1.0,0.0)), f - vec2(1.0,0.0)), u.x),',
    '             mix(dot(hash2(i + vec2(0.0,1.0)), f - vec2(0.0,1.0)),',
    '                 dot(hash2(i + vec2(1.0,1.0)), f - vec2(1.0,1.0)), u.x), u.y);',
    '}',

    'float fbm(vec2 p){',
    '  float v = 0.0;',
    '  float a = 0.5;',
    '  mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);',
    '  for(int i = 0; i < 5; i++){',
    '    v += a * noise(p);',
    '    p = rot * p * 2.02;',
    '    a *= 0.5;',
    '  }',
    '  return v;',
    '}',

    // --- voronoi edge distance: the crack skeleton ------------------------
    // Distance to the boundary between the two nearest cell centres gives a
    // clean, branching seam rather than a blobby noise ridge.
    'float crackDist(vec2 p){',
    '  vec2 n = floor(p);',
    '  vec2 f = fract(p);',
    '  float d1 = 8.0;',
    '  float d2 = 8.0;',
    '  for(int j = -1; j <= 1; j++){',
    '    for(int i = -1; i <= 1; i++){',
    '      vec2 g = vec2(float(i), float(j));',
    '      vec2 o = 0.5 + 0.5 * hash2(n + g);',
    '      float d = length(g + o - f);',
    '      if(d < d1){ d2 = d1; d1 = d; }',
    '      else if(d < d2){ d2 = d; }',
    '    }',
    '  }',
    '  return d2 - d1;',
    '}',

    'void main(){',
    // Cinemagraph: el MISMO paisaje (la foto) queda quieto; solo el AGUA se
    // mueve. Petición del autor (sep-2026): "el mismo paisaje, pero que se
    // mueva el agua con el scroll".
    '  vec2 uv = gl_FragCoord.xy / uRes;',            // 0..1, y desde abajo
    '  vec2 tuv = vec2(uv.x, 1.0 - uv.y);',           // coords de textura (y arriba)
    '  float t = uTime * 0.5;',
    '  float dive = clamp(uProgress, 0.0, 1.0);',

    // Máscara de agua: 1 debajo de la línea de agua, 0 arriba (cielo/orilla).
    '  float water = smoothstep(uWaterLine + 0.10, uWaterLine - 0.10, uv.y);',

    // Ondas: dos trenes cruzados + fbm para que no sea mecánico. La amplitud
    // crece con el scroll (uProgress): el agua se mueve más al bajar.
    '  float amp = (0.0045 + dive * 0.0090) * water;',
    '  float w1 = sin(uv.x * 16.0 + t * 2.2 + fbm(uv * 5.0 + t) * 3.0);',
    '  float w2 = sin(uv.y * 34.0 - t * 1.6 + uPointer.x * 2.0);',
    '  vec2 disp = vec2(w1 * 0.6, (w1 + w2) * 0.5) * amp;',

    '  vec3 col;',
    '  if (uHasPhoto > 0.5) {',
    '    col = texture(uPhoto, tuv + disp).rgb;',
    // Destello cáustico muy leve solo sobre el agua: vida, sin ensuciar la foto.
    '    float caustic = pow(max(0.0, fbm((tuv + disp) * 9.0 + vec2(0.0, t * 0.4))), 2.0);',
    '    col += uGoldLight * caustic * (0.06 + dive * 0.06) * water * uIntensity;',
    '  } else {',
    // Sin foto: degradado azul de respaldo, nunca un hueco negro.
    '    col = mix(uDeep, uInk, uv.y);',
    '  }',

    // Dither. Banding across a large dark gradient is very visible on OLED.
    '  float dither = (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;',
    '  col += dither;',

    '  outColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      if (window.console) console.warn('[kintsugi] shader: ' + gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function hexToRgb(hex) {
    if (!hex) return [0, 0, 0];
    hex = String(hex).trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var n = parseInt(hex, 16);
    if (isNaN(n)) return [0, 0, 0];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function Kintsugi(canvas, opts) {
    var gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: 'low-power',
      failIfMajorPerformanceCaveat: false
    });
    if (!gl) return null;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      if (window.console) console.warn('[kintsugi] link: ' + gl.getProgramInfoLog(prog));
      return null;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var u = {};
    ['uRes', 'uTime', 'uProgress', 'uPointer', 'uInk', 'uDeep', 'uGold', 'uGoldLight', 'uSilt', 'uIntensity',
     'uPhoto', 'uHasPhoto', 'uWaterLine']
      .forEach(function (name) { u[name] = gl.getUniformLocation(prog, name); });

    gl.uniform3fv(u.uInk, hexToRgb(opts.ink));
    gl.uniform3fv(u.uDeep, hexToRgb(opts.deep));
    gl.uniform3fv(u.uGold, hexToRgb(opts.gold));
    gl.uniform3fv(u.uGoldLight, hexToRgb(opts.goldLight));
    gl.uniform3fv(u.uSilt, hexToRgb(opts.silt));
    gl.uniform1f(u.uIntensity, opts.intensity == null ? 1 : opts.intensity);
    gl.uniform1f(u.uWaterLine, opts.waterLine == null ? 0.5 : opts.waterLine);
    gl.uniform1f(u.uHasPhoto, 0);

    // El paisaje real como textura. Se carga aparte; hasta que llega, el shader
    // pinta el degradado de respaldo (uHasPhoto = 0). CORS: la imagen del CDN de
    // Shopify se pide con crossOrigin para poder subirla a la GPU sin "tainted".
    var tex = null;
    if (opts.photo) {
      tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      // Pixel provisional 1x1 para que el sampler sea válido desde el frame 0.
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([12, 43, 59, 255]));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.uniform1i(u.uPhoto, 0);
      var photoImg = new Image();
      photoImg.crossOrigin = 'anonymous';
      photoImg.onload = function () {
        try {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, tex);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, photoImg);
          gl.uniform1f(u.uHasPhoto, 1);
          if (state && !state.running && !state.lost) requestAnimationFrame(frame);
        } catch (e) {
          if (window.console) console.warn('[kintsugi] texture upload failed (CORS?): ' + e.message);
        }
      };
      photoImg.src = opts.photo;
    }

    var dprCap = opts.dprCap || 1.75;
    var state = {
      progress: 0,
      pointer: [0, 0],
      pointerTarget: [0, 0],
      running: false,
      visible: true,
      lost: false,
      raf: 0,
      t0: 0,
      // Cap the simulated clock so a backgrounded tab does not resume with a
      // giant dt and jump the pattern.
      clock: 0
    };

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, dprCap);
      var w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(u.uRes, w, h);
    }

    function frame(now) {
      if (state.lost) return;
      if (!state.t0) state.t0 = now;
      var dt = Math.min((now - state.t0) / 1000, 0.05);
      state.t0 = now;
      state.clock += dt;

      // Ease the pointer so a fast flick does not snap the warp.
      state.pointer[0] += (state.pointerTarget[0] - state.pointer[0]) * 0.06;
      state.pointer[1] += (state.pointerTarget[1] - state.pointer[1]) * 0.06;

      resize();
      gl.uniform1f(u.uTime, state.clock);
      gl.uniform1f(u.uProgress, state.progress);
      gl.uniform2f(u.uPointer, state.pointer[0], state.pointer[1]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (state.running) state.raf = requestAnimationFrame(frame);
    }

    var api = {
      start: function () {
        if (state.running || state.lost) return;
        state.running = true;
        state.t0 = 0;
        state.raf = requestAnimationFrame(frame);
      },
      stop: function () {
        state.running = false;
        cancelAnimationFrame(state.raf);
      },
      setProgress: function (p) {
        state.progress = Math.max(0, Math.min(1, p));
        // Still repaint one frame when parked, so scrubbing works with the
        // loop stopped (motion level "reduced").
        if (!state.running && !state.lost) requestAnimationFrame(frame);
      },
      setPointer: function (x, y) {
        state.pointerTarget[0] = x;
        state.pointerTarget[1] = y;
      },
      resize: resize,
      destroy: function () {
        api.stop();
        state.lost = true;
        gl.deleteProgram(prog);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
        gl.deleteBuffer(buf);
        if (tex) gl.deleteTexture(tex);
        var ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
      },
      _markLost: function () { state.lost = true; api.stop(); },
      _state: state
    };

    resize();
    // Paint one frame immediately so the canvas is never a black hole while we
    // wait for the first rAF.
    gl.uniform1f(u.uTime, 0);
    gl.uniform1f(u.uProgress, 0);
    gl.uniform2f(u.uPointer, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    return api;
  }

  /* ------------------------------------------------------------------------
     Behaviour wiring
     ------------------------------------------------------------------------ */

  function mount(el) {
    var Motion = window.Motion;
    var canvas = el.querySelector('.hero__canvas');
    if (!canvas) return;

    var settings = (window.theme && window.theme.settings) || {};
    if (settings.webgl === false) return;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile && settings.webglMobile === false) return;

    // Motion "off" gets no GPU work at all.
    if (Motion && !Motion.enabled()) return;

    var cs = getComputedStyle(document.documentElement);
    var instance = Kintsugi(canvas, {
      ink: el.dataset.glInk || cs.getPropertyValue('--c-ink'),
      deep: el.dataset.glDeep || cs.getPropertyValue('--c-slate'),
      gold: el.dataset.glGold || cs.getPropertyValue('--c-accent'),
      goldLight: el.dataset.glGoldLight || cs.getPropertyValue('--c-accent-light'),
      // El limo de la ciénaga. Verde de la propia paleta, no un color nuevo.
      silt: el.dataset.glSilt || cs.getPropertyValue('--c-leaf') || '#4C6B3C',
      intensity: parseFloat(el.dataset.glIntensity || '1'),
      // El paisaje real (la foto del hero) y dónde empieza el agua en ella.
      photo: el.dataset.glPhoto || null,
      waterLine: el.dataset.glWaterLine ? parseFloat(el.dataset.glWaterLine) : 0.5,
      dprCap: isMobile ? 1.5 : 1.75
    });

    if (!instance) return; // CSS gradient hero stands in

    canvas.setAttribute('data-ready', 'true');

    var full = !Motion || Motion.full();
    if (full) instance.start();
    else instance.setProgress(0.6); // static, already-repaired frame

    // Pause when off-screen. A hero shader running behind three screens of
    // text is pure battery drain.
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        function (entries) {
          var visible = entries[0].isIntersecting;
          if (visible && full) instance.start();
          else instance.stop();
        },
        { rootMargin: '10% 0px' }
      );
      io.observe(el);
    }

    function onVisibility() {
      if (document.hidden) instance.stop();
      else if (full) instance.start();
    }
    document.addEventListener('visibilitychange', onVisibility);

    // Pointer influence: desktop only. On touch it would fight the scroll.
    var onPointer = null;
    if (window.matchMedia('(hover: hover)').matches) {
      onPointer = function (e) {
        var r = el.getBoundingClientRect();
        instance.setPointer(
          ((e.clientX - r.left) / r.width) * 2 - 1,
          ((e.clientY - r.top) / r.height) * 2 - 1
        );
      };
      el.addEventListener('pointermove', onPointer, { passive: true });
    }

    // Scroll drives the gold fill.
    var st = null;
    if (typeof window.ScrollTrigger !== 'undefined') {
      st = window.ScrollTrigger.create({
        trigger: el,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        onUpdate: function (self) { instance.setProgress(self.progress); }
      });
    }

    function onLost(e) {
      e.preventDefault();
      instance._markLost();
      canvas.removeAttribute('data-ready');
    }
    canvas.addEventListener('webglcontextlost', onLost);

    var onResize = function () { instance.resize(); };
    window.addEventListener('resize', onResize);

    return function () {
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      if (onPointer) el.removeEventListener('pointermove', onPointer);
      canvas.removeEventListener('webglcontextlost', onLost);
      window.removeEventListener('resize', onResize);
      if (st) st.kill();
      instance.destroy();
    };
  }

  function register() {
    if (window.Motion && window.Motion.define) {
      window.Motion.define('kintsugi-hero', mount);
      // motion.js may already have mounted the page before this file parsed.
      if (window.Motion.ready) window.Motion.mount(document);
    }
  }

  if (window.Motion) register();
  else document.addEventListener('DOMContentLoaded', register);

  window.Kintsugi = Kintsugi;
})();
